import React, { useState, useRef, useEffect } from 'react';
import { X, Copy, Check, AlertCircle } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface Coordinate {
  x: number;
  y: number;
  label: string;
}

interface Form940MapperProps {
  onClose: () => void;
  selectedYear: number;
}

const Form940Mapper: React.FC<Form940MapperProps> = ({ onClose, selectedYear }) => {
  const [coordinates, setCoordinates] = useState<Coordinate[]>([]);
  const [currentLabel, setCurrentLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const [copied, setCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-irs-form?form=f940&year=${selectedYear}`;

      console.log('Fetching PDF from:', apiUrl);

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch IRS Form 940: ${response.status}`);
      }

      const formPdfBytes = await response.arrayBuffer();
      console.log('PDF bytes received:', formPdfBytes.byteLength);

      const loadingTask = pdfjsLib.getDocument({ data: formPdfBytes });
      const pdf = await loadingTask.promise;
      console.log('PDF loaded, pages:', pdf.numPages);

      setPdfDoc(pdf);

    } catch (err) {
      console.error('Error loading PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to load PDF');
    } finally {
      setLoading(false);
    }
  };

  const renderPage = async () => {
    if (!pdfDoc) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.5 });

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      setPdfDimensions({
        width: viewport.width / 1.5,
        height: viewport.height / 1.5
      });

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      console.log('Page rendered successfully');
    } catch (err) {
      console.error('Error rendering page:', err);
      setError(err instanceof Error ? err.message : 'Failed to render page');
    }
  };

  useEffect(() => {
    loadPDF();
  }, []);

  useEffect(() => {
    if (pdfDoc) {
      renderPage();
    }
  }, [pdfDoc, currentPage]);

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!currentLabel.trim()) {
      alert('Please enter a label first');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const scaleX = pdfDimensions.width / (rect.width / 1.5);
    const scaleY = pdfDimensions.height / (rect.height / 1.5);

    const pdfX = Math.round(x * scaleX);
    const pdfY = Math.round(y * scaleY);

    setCoordinates([...coordinates, { x: pdfX, y: pdfY, label: currentLabel }]);
    setCurrentLabel('');
  };

  const removeCoordinate = (index: number) => {
    setCoordinates(coordinates.filter((_, i) => i !== index));
  };

  const generateCode = () => {
    let code = '// Coordinate mappings for Form 940\n';
    code += '// Copy this into your handleDownloadForm940 function\n\n';

    const page1Coords = coordinates.filter(c => c.label.includes('_page1_') || !c.label.includes('_page'));
    const page2Coords = coordinates.filter(c => c.label.includes('_page2_'));

    if (page1Coords.length > 0) {
      code += '// Page 1\n';
      page1Coords.forEach((coord) => {
        code += `drawText(firstPage, ${coord.label}, ${coord.x}, ${coord.y});\n`;
      });
    }

    if (page2Coords.length > 0) {
      code += '\n// Page 2\n';
      page2Coords.forEach((coord) => {
        code += `drawText(secondPage, ${coord.label}, ${coord.x}, ${coord.y});\n`;
      });
    }

    return code;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generateCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-[98vw] max-h-[98vh] overflow-hidden flex flex-col">
        <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-semibold text-gray-900">Form 940 Coordinate Mapper</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-auto min-h-0">
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={currentLabel}
                    onChange={(e) => setCurrentLabel(e.target.value)}
                    placeholder="e.g., form940Data.businessName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a label, then click on the PDF where you want to place this field
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 bg-blue-50">
                  <p className="text-sm text-blue-900 font-medium mb-2">Instructions:</p>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Enter a field label (add _page1_ or _page2_ suffix to organize by page)</li>
                    <li>Click on the PDF preview where you want to place that field</li>
                    <li>Switch pages using the buttons below the preview</li>
                    <li>Repeat for all fields you want to map</li>
                    <li>Copy the generated code when done</li>
                  </ol>
                </div>
              </div>

              <div>
                <div className="mb-3 flex justify-between items-center">
                  <h4 className="font-semibold text-gray-900">
                    Mapped Coordinates ({coordinates.length})
                  </h4>
                  <button
                    onClick={copyToClipboard}
                    disabled={coordinates.length === 0}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                  </button>
                </div>

                <div className="space-y-2 mb-3 overflow-auto" style={{ maxHeight: '160px' }}>
                  {coordinates.map((coord, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{coord.label}</div>
                        <div className="text-xs text-gray-600">
                          x: {coord.x}, y: {coord.y}
                        </div>
                      </div>
                      <button
                        onClick={() => removeCoordinate(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The PDF dimensions are {pdfDimensions.width} x{' '}
                    {pdfDimensions.height}. Use these coordinates in your drawText function.
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-100">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-600">Loading PDF...</div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                  <div className="text-red-600 text-center mb-4">{error}</div>
                  <button
                    onClick={loadPDF}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div className="overflow-auto flex justify-center" style={{ minHeight: '1375px', minWidth: '1063px' }}>
                    <canvas
                      ref={canvasRef}
                      onClick={handleCanvasClick}
                      className="cursor-crosshair"
                    />
                  </div>
                  <div className="border-t border-gray-300 bg-white px-4 py-2 flex items-center justify-center space-x-4">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-1.5 rounded ${
                        currentPage === 1
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Page 1
                    </button>
                    <button
                      onClick={() => setCurrentPage(2)}
                      disabled={currentPage === 2}
                      className={`px-4 py-1.5 rounded ${
                        currentPage === 2
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Page 2
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="mt-4 bg-gray-900 rounded-lg p-4 overflow-auto" style={{ maxHeight: '200px' }}>
              <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                {generateCode()}
              </pre>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form940Mapper;
